import React from 'react';

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Box,
} from '@mui/material';

import { ReactComponent as CheckmarkIconWhite } from '../../../common/assets/icons/checkmark_icon_white.svg';
import PaginationControl from '../../../common/components/PaginationControl';
import { Code } from '../../../common/types/Codes';

const cptColumns = [
  '',
  'Description',
  'Work RVU',
  'Non-Facility Total',
  'Facility Total',
  '',
];

const hcpcsColumns = ['', 'Description', ''];

const AddCodeTableHead: React.FC<{
  type: string;
}> = ({ type }) => {
  return (
    <TableHead
      sx={{
        backgroundColor: (theme) => theme.palette.divider,
        height: '59px',
        borderRadius: '12px',
      }}
    >
      <TableRow>
        {(type === 'CPT' ? cptColumns : hcpcsColumns).map((label, index) => (
          <TableCell key={index}>
            <Typography sx={{ fontSize: '13px' }} color='text.secondary'>
              {label}
            </Typography>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

type Props = {
  codes: Code[];
  selectedCodes: Code[];
  didSelectCode: (code: Code) => void;
  onClose: () => void;
  isClinicNote: boolean;
  type: 'CPT' | 'HCPCS' | 'ICD10';
};

const AddCodeTable: React.FC<Props> = ({
  codes,
  didSelectCode,
  onClose,
  selectedCodes,
  isClinicNote = false,
  type,
}) => {
  const [page, setPage] = React.useState(0);
  const rowsPerPage = 5;

  const isSelected = (code: Code) => {
    return selectedCodes.findIndex((c) => c.code === code.code) !== -1;
  };

  const emptyRows =
    codes.length > 0
      ? page > 0
        ? Math.max(0, (1 + page) * rowsPerPage - codes.length)
        : 0
      : rowsPerPage;

  const addToText = isClinicNote ? 'Add to Clinic Note' : 'Add to Op Note';

  return (
    <>
      <TableContainer>
        <Table>
          <AddCodeTableHead type={type} />
          <TableBody>
            {codes
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((code) => (
                <TableRow
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  key={code.code}
                  itemID={`tr-${code.code}`}
                >
                  <TableCell>{code.code}</TableCell>
                  <TableCell>{code.description}</TableCell>
                  {type === 'CPT' && (
                    <>
                      <TableCell>{code.work_rvu ?? '-'}</TableCell>
                      <TableCell>{code.non_facility_total ?? '-'}</TableCell>
                      <TableCell>{code.facility_total ?? '-'}</TableCell>
                    </>
                  )}

                  <TableCell
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Button
                      sx={{
                        borderRadius: '40px',
                        flexShrink: 0,
                        backgroundColor: 'appGreen',
                        minWidth: '101px',
                        height: '36px',
                        marginLeft: 'auto',
                      }}
                      onClick={() => didSelectCode(code)}
                      color={!isSelected(code) ? 'appGray' : 'appGreen'}
                      variant={!isSelected(code) ? 'outlined' : 'contained'}
                      startIcon={
                        !isSelected(code) ? undefined : <CheckmarkIconWhite />
                      }
                    >
                      <Typography
                        noWrap
                        color={!isSelected(code) ? 'secondary' : 'white'}
                      >
                        {!isSelected(code) ? addToText : 'Added'}
                      </Typography>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            {emptyRows > 0 && (
              <TableRow
                style={{
                  height: 73 * emptyRows,
                }}
              >
                <TableCell sx={{ border: 0 }} colSpan={5} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box className='row' sx={{ justifyContent: 'space-between' }}>
        <PaginationControl
          count={Math.ceil(codes.length / rowsPerPage)}
          onChange={(_e, p) => setPage(p - 1)}
          page={page + 1}
        />
        <Button
          onClick={onClose}
          disableElevation
          color='primary'
          variant='contained'
          sx={{
            borderRadius: '40px',
            flexShrink: 0,
            minWidth: '101px',
            height: '36px',
            mx: '16px',
          }}
        >
          <Typography color='white'>Done</Typography>
        </Button>
      </Box>
    </>
  );
};

export default AddCodeTable;
